import Container from './Container';
import Overlay from './Overlay';
import { MessageTypes } from '../types/MessageTypes';

class Popup {
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private resizeListener: (() => void) | null = null;
  private keydownListener: ((event: KeyboardEvent) => void) | null = null;
  private lastPositionData: {
    dimensions: { width: number; height: number };
    closeOnOutsideClick?: boolean;
  } | null = null;

  constructor() {
    this.startListeners();
  }

  private calculateIframePosition(dimensions: { width: number; height: number }): {
    top: string;
    left: string;
  } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 20;

    const top = `${Math.max(padding, (viewportHeight - dimensions.height) / 2)}px`;
    const left = `${Math.max(padding, (viewportWidth - dimensions.width) / 2)}px`;

    return { top, left };
  }

  private sendClosePopup = () => {
    Container.getInstance().sendMessage({
      type: MessageTypes.CLOSE_POPUP
    });
  };

  private syncOutsideClose(closeOnOutsideClick?: boolean) {
    this.clearKeydownListener();

    if (closeOnOutsideClick) {
      Overlay.getInstance().setOutsideCloseHandler(this.sendClosePopup);
      this.keydownListener = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.sendClosePopup();
        }
      };
      window.addEventListener('keydown', this.keydownListener);
    } else {
      Overlay.getInstance().setOutsideCloseHandler(null);
    }
  }

  private clearKeydownListener() {
    if (this.keydownListener) {
      window.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }
  }

  private handleResize = () => {
    if (this.lastPositionData) {
      const calculatedPosition = this.calculateIframePosition(this.lastPositionData.dimensions);

      Container.getInstance().resizeAndPosition(calculatedPosition, {
        width: `${this.lastPositionData.dimensions.width}px`,
        height: `${this.lastPositionData.dimensions.height}px`
      });

      Overlay.getInstance().show();
      this.syncOutsideClose(this.lastPositionData.closeOnOutsideClick);
    }
  };

  private startListeners() {
    Container.getInstance().sendMessage({
      type: MessageTypes.RUNNING_POPUP
    });

    this.messageListener = async (event: MessageEvent) => {
      if (event.data.type === MessageTypes.RESIZE_AND_POSITION_IFRAME) {
        const { dimensions, closeOnOutsideClick } = event.data;

        this.lastPositionData = { dimensions, closeOnOutsideClick: !!closeOnOutsideClick };

        const calculatedPosition = this.calculateIframePosition(dimensions);

        Container.getInstance().resizeAndPosition(calculatedPosition, {
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`
        });

        Container.getInstance().show();

        Overlay.getInstance().show();
        this.syncOutsideClose(!!closeOnOutsideClick);
      }
    };

    this.resizeListener = this.handleResize;

    window.addEventListener('message', this.messageListener);
    window.addEventListener('resize', this.resizeListener);
  }

  public destroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }

    this.clearKeydownListener();
    Overlay.getInstance().setOutsideCloseHandler(null);

    this.lastPositionData = null;
  }
}

export default Popup;
